import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

function Matches({ onChatOpen }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [lastMessages, setLastMessages] = useState({});

  useEffect(() => {
    getCurrentUser();
    fetchMatches();
  }, []);

  // Fetch unread counts and last messages when matches are loaded
  useEffect(() => {
    if (matches.length > 0 && currentUser) {
      fetchUnreadCounts();
      fetchLastMessages();
      
      // Set up interval to check for new messages and updates
      const interval = setInterval(() => {
        fetchUnreadCounts();
        fetchLastMessages();
      }, 5000);
      
      // Listen for custom refresh event
      const handleRefresh = () => {
        console.log('Refreshing unread counts due to custom event');
        fetchUnreadCounts();
        fetchLastMessages();
      };
      
      window.addEventListener('refreshUnreadCounts', handleRefresh);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('refreshUnreadCounts', handleRefresh);
      };
    }
  }, [matches, currentUser]);

  const getCurrentUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setCurrentUser(user);
  };

  const fetchUnreadCounts = async () => {
    if (!currentUser || matches.length === 0) return;

    try {
      const unreadCountsObj = {};
      
      for (const match of matches) {
        // Get all messages from this match
        const { data: messages } = await supabase
          .from('messages')
          .select('id')
          .eq('sender_id', match.id)
          .eq('receiver_id', currentUser.id);

        if (messages && messages.length > 0) {
          // Get read messages
          const messageIds = messages.map(m => m.id);
          const { data: readMessages } = await supabase
            .from('message_reads')
            .select('message_id')
            .eq('user_id', currentUser.id)
            .in('message_id', messageIds);

          const readMessageIds = readMessages?.map(r => r.message_id) || [];
          const unreadCount = messages.filter(m => !readMessageIds.includes(m.id)).length;
          
          if (unreadCount > 0) {
            unreadCountsObj[match.id] = unreadCount;
          }
        }
      }
      
      setUnreadCounts(unreadCountsObj);
    } catch (error) {
      console.error('Error fetching unread counts:', error);
    }
  };

  // Fetch last message and timestamp for each conversation
  const fetchLastMessages = async () => {
    if (!currentUser || matches.length === 0) return;

    try {
      const lastMessagesObj = {};
      
      for (const match of matches) {
        // Get the most recent message in this conversation
        const { data: lastMessage } = await supabase
          .from('messages')
          .select('content, created_at, sender_id, message_type, gift_data')
          .or(`and(sender_id.eq.${currentUser.id},receiver_id.eq.${match.id}),and(sender_id.eq.${match.id},receiver_id.eq.${currentUser.id})`)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (lastMessage) {
          lastMessagesObj[match.id] = {
            content: lastMessage.content || 'Photo',
            timestamp: lastMessage.created_at,
            isFromMe: lastMessage.sender_id === currentUser.id,
            messageType: lastMessage.message_type || 'text',
            giftData: lastMessage.gift_data
          };
        } else {
          lastMessagesObj[match.id] = {
            content: 'Start a conversation!',
            timestamp: match.relationship_date,
            isFromMe: false,
            messageType: 'text'
          };
        }
      }
      
      setLastMessages(lastMessagesObj);
    } catch (error) {
      console.error('Error fetching last messages:', error);
    }
  };

  const fetchMatches = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get both matches and friends in parallel
      const [matchesResult, friendsResult] = await Promise.all([
        // Get matches
        Promise.all([
          supabase.from('matches').select('liked_user_id').eq('user_id', user.id),
          supabase.from('matches').select('user_id').eq('liked_user_id', user.id)
        ]),
        // Get friends
        supabase
          .from('friendships')
          .select('*')
          .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
          .eq('status', 'accepted')
      ]);

      let allChatPartners = [];

      // Process matches
      const [myLikes, likesOnMe] = matchesResult;
      const myLikedIds = myLikes.data?.map(like => like.liked_user_id) || [];
      const whoLikedMeIds = likesOnMe.data?.map(like => like.user_id) || [];
      const mutualMatchIds = myLikedIds.filter(id => whoLikedMeIds.includes(id));

      if (mutualMatchIds.length > 0) {
        const { data: matchedProfiles } = await supabase
          .from('profiles')
          .select('*')
          .in('id', mutualMatchIds);

        const matches = (matchedProfiles || []).map(profile => ({
          ...profile,
          relationship_type: 'match',
          relationship_date: new Date().toISOString() // We don't have exact match date
        }));

        allChatPartners = [...allChatPartners, ...matches];
      }

      // Process friends
      if (friendsResult.data && friendsResult.data.length > 0) {
        const friendIds = friendsResult.data.map(friendship => {
          return friendship.requester_id === user.id 
            ? friendship.addressee_id 
            : friendship.requester_id;
        });

        if (friendIds.length > 0) {
          const { data: friendProfiles } = await supabase
            .from('profiles')
            .select('*')
            .in('id', friendIds);

          if (friendProfiles) {
            const friends = friendProfiles.map(profile => {
              const friendship = friendsResult.data.find(f => 
                f.requester_id === profile.id || f.addressee_id === profile.id
              );
              return {
                ...profile,
                relationship_type: 'friend',
                relationship_date: friendship.updated_at
              };
            });

            allChatPartners = [...allChatPartners, ...friends];
          }
        }
      }

      setMatches(allChatPartners);

    } catch (error) {
      console.error('Error fetching matches and friends:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get conversations sorted by most recent message
  const getSortedConversations = () => {
    return matches
      .map(match => ({
        ...match,
        lastMessage: lastMessages[match.id],
        unreadCount: unreadCounts[match.id] || 0
      }))
      .sort((a, b) => {
        const aTime = a.lastMessage?.timestamp || a.relationship_date;
        const bTime = b.lastMessage?.timestamp || b.relationship_date;
        return new Date(bTime) - new Date(aTime);
      });
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now - messageTime) / (1000 * 60));
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 60) {
      return diffInMinutes <= 1 ? 'Just now' : `${diffInMinutes}m`;
    } else if (diffInHours < 24) {
      return `${diffInHours}h`;
    } else if (diffInDays < 7) {
      return `${diffInDays}d`;
    } else {
      return messageTime.toLocaleDateString();
    }
  };

  // Format message content for preview
  const formatMessagePreview = (lastMessage) => {
    if (!lastMessage) return 'Start a conversation!';
    
    if (lastMessage.messageType === 'gift' && lastMessage.giftData) {
      const prefix = lastMessage.isFromMe ? 'You sent' : 'Received';
      return `${prefix} ${lastMessage.giftData.name || 'a gift'}`;
    } else if (lastMessage.messageType === 'photo') {
      const prefix = lastMessage.isFromMe ? 'You sent' : 'Sent';
      return `${prefix} a photo`;
    } else {
      const content = lastMessage.content || 'Message';
      const truncated = content.length > 50 ? content.substring(0, 50) + '...' : content;
      return lastMessage.isFromMe ? `You: ${truncated}` : truncated;
    }
  };

  const handleChatOpen = (match) => {
    // Clear unread count for this match when opening chat
    setUnreadCounts(prev => {
      const newCounts = { ...prev };
      delete newCounts[match.id];
      return newCounts;
    });
    onChatOpen(match);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading your matches...
      </div>
    );
  }

  const sortedConversations = getSortedConversations();

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '0'
    }}>
      <div style={{ 
        maxWidth: '500px', 
        margin: '0 auto',
        background: 'rgba(255, 255, 255, 0.03)',
        minHeight: '100vh',
        backdropFilter: 'blur(10px)',
        borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Header */}
        <div style={{ 
          position: 'sticky',
          top: 0,
          background: 'rgba(26, 26, 46, 0.95)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '20px 25px',
          zIndex: 100
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h1 style={{ 
              fontSize: '28px', 
              fontWeight: '700',
              background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              margin: 0
            }}>
              Let's Flirt
            </h1>
            <div style={{
              fontSize: '14px',
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: '500'
            }}>
              {matches.filter(m => m.relationship_type === 'match').length}M • {matches.filter(m => m.relationship_type === 'friend').length}F
            </div>
          </div>
        </div>

        {/* Conversations List */}
        {matches.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '60px 25px',
          }}>
            <div style={{ fontSize: '60px', marginBottom: '20px' }}>💬</div>
            <h2 style={{ 
              color: '#00E9FF', 
              marginBottom: '15px',
              fontSize: '20px',
              fontWeight: '600'
            }}>
              No conversations yet
            </h2>
            <p style={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '14px',
              lineHeight: '1.5',
              margin: 0
            }}>
              Start matching and make new friends to begin chatting!
            </p>
          </div>
        ) : (
          <div style={{ padding: '0' }}>
            {sortedConversations.map((conversation) => {
              const lastMessage = conversation.lastMessage;
              const hasUnread = conversation.unreadCount > 0;
              
              return (
                <div 
                  key={conversation.id} 
                  onClick={() => handleChatOpen(conversation)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 25px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease',
                    background: hasUnread ? 'rgba(0, 233, 255, 0.05)' : 'transparent',
                    position: 'relative'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.08)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = hasUnread ? 'rgba(0, 233, 255, 0.05)' : 'transparent';
                  }}
                >
                  {/* Profile Picture */}
                  <div style={{ 
                    position: 'relative', 
                    marginRight: '16px',
                    flexShrink: 0
                  }}>
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      background: conversation.photo_url 
                        ? `url(${conversation.photo_url})` 
                        : 'linear-gradient(135deg, #00E9FF, #8A2BE2)',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '24px',
                      fontWeight: '600',
                      color: 'white',
                      border: hasUnread ? '2px solid #00E9FF' : '2px solid rgba(255, 255, 255, 0.1)',
                      boxShadow: hasUnread ? '0 0 10px rgba(0, 233, 255, 0.3)' : 'none'
                    }}>
                      {!conversation.photo_url && (conversation.name || 'U').charAt(0).toUpperCase()}
                    </div>
                    
                    {/* Relationship type badge */}
                    <div style={{
                      position: 'absolute',
                      bottom: '-2px',
                      right: '-2px',
                      width: '20px',
                      height: '20px',
                      borderRadius: '50%',
                      background: conversation.relationship_type === 'match' 
                        ? 'linear-gradient(135deg, #FF6B6B, #FF8E8E)' 
                        : 'linear-gradient(135deg, #4ECDC4, #44A08D)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '10px',
                      border: '2px solid #1a1a2e'
                    }}>
                      {conversation.relationship_type === 'match' ? '💕' : '👥'}
                    </div>
                  </div>

                  {/* Message Content */}
                  <div style={{ 
                    flex: 1, 
                    minWidth: 0,
                    paddingRight: '12px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '4px'
                    }}>
                      <h3 style={{
                        fontSize: '16px',
                        fontWeight: hasUnread ? '700' : '600',
                        color: hasUnread ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '200px'
                      }}>
                        {conversation.name || 'Unknown User'}
                      </h3>
                      
                      <div style={{
                        fontSize: '12px',
                        color: hasUnread ? '#00E9FF' : 'rgba(255, 255, 255, 0.5)',
                        fontWeight: hasUnread ? '600' : '400',
                        flexShrink: 0
                      }}>
                        {formatTimestamp(lastMessage?.timestamp || conversation.relationship_date)}
                      </div>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <p style={{
                        fontSize: '14px',
                        color: hasUnread ? 'rgba(255, 255, 255, 0.8)' : 'rgba(255, 255, 255, 0.6)',
                        margin: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontWeight: hasUnread ? '500' : '400',
                        flex: 1
                      }}>
                        {formatMessagePreview(lastMessage)}
                      </p>
                      
                      {/* Unread badge */}
                      {hasUnread && (
                        <div style={{
                          background: '#ff4458',
                          color: 'white',
                          borderRadius: '50%',
                          minWidth: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '11px',
                          fontWeight: '700',
                          marginLeft: '8px',
                          boxShadow: '0 2px 8px rgba(255, 68, 88, 0.4)'
                        }}>
                          {conversation.unreadCount > 99 ? '99+' : conversation.unreadCount}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Matches;